import { GetStaticProps } from 'next';
import { useEffect, useState } from 'react';
import Image from 'next/image';

import { ALL_MENU_QUERY, MenuLocationEnum } from '@/data/allMenuQuery';
import { flatListToHierarchical } from '@/utils/flatListToHierarchical';
import { getApolloClient } from '@/utils/apollo-client';
import { transformMenus } from '@/utils/transformMenus';
import { withLayout } from '@/layout/Layout';
// import { Slider } from '@/components/common/Slider';

import { LayoutProps } from '@/layout/Layout/Layout.props';
import { PageProps } from '@/types/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { ABOUT_US_PAGE_QUERY } from '@/data/aboutUsPageQuery';
import { Hero } from '@/components/common/Hero';
import { Heading } from '@/components/typography';
import { Section } from '@/components/common/Section';
import { footerDataTransformer } from '@/utils/footerDataTransformer';
import {
  FooterMenuLocationEnum,
  FOOTER_DATA_QUERY,
  FOOTER_DATA_QUERY_EN,
} from '@/data/footerDataQuery';
import { motion } from 'framer-motion';
import { AnimatedCard } from '@/components/animation/AnimatedCard';
import { PageHead } from '@/components/common/PageHead';

// import { exampleBreadcrumbs } from '@/templates/breadcrumbsTpl';

const Page = ({ data }: PageProps & LayoutProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const {
    featuredImage,
    seo,
    aboutUsPage: { factsAboutUs, ourCoordinates /* partners */ },
    hero: { hero },
  } = data.pageBy.translation;

  const featureImage = featuredImage?.node;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <>
      <PageHead
        title={seo?.title || hero?.title}
        seo={seo || {}}
        canonicalUrl={seo?.canonicalUrl}
        breadcrumbs={seo?.breadcrumbs}
      />
      {isMounted && (
        <>
          <Hero
            isAccent
            className="pb-[60px] md:pb-32 lg:pb-[60px]"
            breadcrumbs={seo?.breadcrumbs}
          >
            <motion.div
              className="mb-10 grid gap-10 text-white last:mb-0 dark:text-darkFullDark"
              initial={{ x: -500, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.25 }}
            >
              <Heading tag="h1" content={hero?.title} color="white" />
              {hero?.descrPtion && (
                <p
                  className="text-lg font-bold md:max-w-[90%] md:text-xl lg:max-w-[75%] lg:text-xxl"
                  dangerouslySetInnerHTML={{ __html: hero?.descrPtion }}
                />
              )}
            </motion.div>
            <motion.div
              initial={{ x: 500, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.25 }}
            >
              {featureImage && (
                <div className="self-end">
                  <Image
                    src={featureImage?.mediaItemUrl ?? ''}
                    alt={featureImage?.altText ?? ''}
                    width={featureImage?.mediaDetails?.width}
                    height={featureImage?.mediaDetails?.height}
                  />
                </div>
              )}
              {hero.addCtaBlock && hero.ctaBlock && (
                <div
                  className="hide-bg-image rounded bg-accentMain bg-[bottom_right_-3px] bg-no-repeat px-[30px] pb-[122px] pt-10 text-xl font-bold text-white dark:bg-darkFullLight dark:text-darkFullDark md:bg-contain md:bg-[bottom_right_-15px] md:px-10 md:pb-10 md:text-[28px] md:leading-9 lg:max-w-[90%] lg:px-[60px] lg:py-[60px] lg:text-h3"
                  style={{
                    backgroundImage: `url('${hero.ctaBlock?.bgImage.mediaItemUrl}')`,
                  }}
                >
                  <p className="md:max-w-[80%] lg:max-w-[65%]">
                    {hero.ctaBlock?.text}
                  </p>
                </div>
              )}
            </motion.div>
          </Hero>
          {factsAboutUs.length > 0 && (
            <Section className="pt-[60px] md:pt-20 lg:pt-[100px]">
              <ul className="md:grid md:grid-flow-col md:grid-cols-2 md:grid-rows-[_repeat(3,_min-content)] md:gap-x-10 md:text-lg lg:gap-x-[140px]">
                {factsAboutUs?.map((item, index) => (
                  <li
                    key={item.item}
                    className={`border-l border-primaryLight pb-[60px] pl-8 last:pb-0 lg:pl-16 ${
                      index % 3 === 2 ? 'md:pb-0' : 'md:pb-20 lg:pb-[120px]'
                    }`}
                  >
                    <AnimatedCard className="relative before:absolute before:-left-8 before:top-[3px] before:h-4 before:w-4 before:-translate-x-[53%] before:rotate-45 before:bg-accentMain lg:before:-left-16">
                      {item.item}
                    </AnimatedCard>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {ourCoordinates.list.length > 0 && (
            <Section>
              <Heading tag="h2" withDivider content={ourCoordinates.title} />
              <ul className="grid gap-y-8 md:grid-cols-2 md:items-start md:gap-x-8 lg:grid-cols-4 lg:items-stretch">
                {ourCoordinates.list?.map((item, index) => (
                  <AnimatedCard
                    tag="li"
                    duration={0.5}
                    key={item.item}
                    className={`grid max-w-[76%] gap-5 even:justify-self-end md:max-w-none md:border-l md:border-primaryLight md:pl-8 md:even:mt-[25%] lg:content-start lg:last:border-r lg:last:border-primaryLight lg:last:pr-8 lg:even:mt-0 ${
                      index === 1 ? 'lg:pt-[80%]' : ''
                    } ${index === 2 ? 'lg:pt-[40%]' : ''}`}
                  >
                    <span className="text-xl md:text-h3">
                      {'/0' + (index + 1)}
                    </span>
                    {item.icon?.mediaItemUrl && (
                      <AnimatedCard
                        initial={{
                          opacity: 0,
                          borderRadius: '50%',
                        }}
                        duration={1}
                        animate={{
                          opacity: 1,
                          borderRadius: '0',
                        }}
                        className="h-[60px] w-[60px] overflow-hidden md:h-[80px] md:w-[80px]"
                      >
                        <Image
                          src={item.icon.mediaItemUrl}
                          alt={item.icon.altText}
                          width={item?.icon?.mediaDetails?.width}
                          height={item?.icon?.mediaDetails?.height}
                          className="h-full w-full"
                        />
                      </AnimatedCard>
                    )}
                    <AnimatedCard
                      tag="p"
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      damping={12}
                      stiffness={120}
                      type="spring"
                    >
                      {item.item}
                    </AnimatedCard>
                  </AnimatedCard>
                ))}
              </ul>
            </Section>
          )}

          {/* {partners?.partnersList?.length > 0 && (
            <Section>
              <Heading tag="h2" withDivider content={partners.title} />

              <Slider width={partners.partnersList[0]?.mediaDetails?.width}>
                {partners.partnersList.map((item, index) => (
                  <div key={index} className="block object-center">
                    {item.mediaItemUrl && (
                      <Image
                        src={item.mediaItemUrl}
                        alt={item.altText}
                        width={item?.mediaDetails?.width}
                        height={item?.mediaDetails?.height}
                        className="mx-auto h-[63px] w-[218px] md:h-[80px] lg:w-[280px]"
                      />
                    )}
                  </div>
                ))}
              </Slider>
            </Section>
          )} */}
        </>
      )}
    </>
  );
};

export default withLayout(Page);

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const client = getApolloClient();
  const location =
    locale == 'uk'
      ? MenuLocationEnum.PRIMARY_NAVIGATION
      : MenuLocationEnum.PRIMARY_NAVIGATION___EN;
  const language = locale?.toUpperCase();
  const { data } = await client.query({
    query: ABOUT_US_PAGE_QUERY,
    variables: {
      language,
    },
  });
  const {
    data: { menuItems },
  } = await client.query({
    query: ALL_MENU_QUERY,
    variables: {
      location,
    },
  });
  const footerLocation =
    locale == 'uk'
      ? FooterMenuLocationEnum.FOOTER_MENU
      : FooterMenuLocationEnum.FOOTER_MENU___EN;

  const { data: footerMenuItems } =
    locale == 'uk'
      ? await client.query({
          query: FOOTER_DATA_QUERY,
          variables: {
            location: footerLocation,
          },
        })
      : await client.query({
          query: FOOTER_DATA_QUERY_EN,
          variables: {
            location: footerLocation,
          },
        });
  const footerData = footerDataTransformer(footerMenuItems);

  const flatList = flatListToHierarchical(menuItems.nodes);
  const menus = transformMenus(flatList);

  return {
    props: {
      data,
      menus,
      footerData,
      ...(await serverSideTranslations(locale as string, ['common'])),
    },
  };
};
